import React, { useMemo } from 'react';
import { FormikProps } from 'formik';
import { Row, Col } from 'antd';
import { Input } from 'formik-antd';

import { MainLocation, WorksiteLocationFormValues } from '../WorksiteLocation';
import {
  countryCitiesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  countriesSelector,
  isStatesLoadingSelector,
  statesSelector,
  isCitiesLoadingSelector,
  citiesSelector,
} from 'redux/selectors/regionSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { getBillLocationValue } from '../utils';
import FormItem from 'components/formik/FormItem';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import Checkbox from 'components/formik/Checkbox';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

type Props = {
  formik: FormikProps<WorksiteLocationFormValues>;
  mainLocation: MainLocation;
  disabled?: boolean;
};

export default function AssignmentBillLocationForm(props: Props) {
  const { formik, mainLocation, disabled: isFormDisabled = false } = props;
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const isInternationalCountry = !checkIsCountriesWithStatesDropdown(
    getBillLocationValue(formik, mainLocation, 'country'),
  );

  const filteredStates = useMemo(() => {
    return states.filter(
      (state) =>
        state.countryId ===
        getBillLocationValue(formik, mainLocation, 'country'),
    );
  }, [formik, states, mainLocation]);

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (city) =>
          city.stateId === getBillLocationValue(formik, mainLocation, 'state'),
      ),
    [cities, formik, mainLocation],
  );
  const filteredInternationalCities = useMemo(
    () =>
      countryCities.filter((city) => {
        const country = formik.values.billing.locationSameAsWork
          ? mainLocation.country
          : formik.values.billing.country;
        return city.countryId === country;
      }),
    [
      formik.values.billing.country,
      formik.values.billing.locationSameAsWork,
      countryCities,
      mainLocation.country,
    ],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    formik.setFieldValue('billing.city', '', false);
    formik.setFieldValue('billing.state', undefined, false);
    formik.setFieldValue('billing.internationalCityName', '', false);
    formik.setFieldValue('billing.internationalStateName', '', false);
  };

  const handleStateChange = (
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    if (checkIsCountriesWithStatesDropdown(formik.values.work.country)) {
      formik.setFieldValue('billing.city', '', false);
    } else {
      formik.setFieldValue('billing.internationalCityName', '', false);
    }
  };

  const handleLocationSameAsWorkChange = (
    e: any,
    formik: FormikProps<WorksiteLocationFormValues>,
  ) => {
    if (e.target.checked) return;

    formik.setFieldValue('billing.country', undefined, false);
    formik.setFieldValue('billing.state', undefined, false);
    formik.setFieldValue('billing.internationalStateName', '', false);
    formik.setFieldValue('billing.city', '', false);
    formik.setFieldValue('billing.internationalCityName', '', false);
    formik.setFieldValue('billing.address', '', false);
    formik.setFieldValue('billing.zip', '', false);

    formik.setFieldTouched('billing.country', true, false);
    formik.setFieldTouched('billing.state', true, false);
    formik.setFieldTouched('billing.internationalStateName', true, false);
    formik.setFieldTouched('billing.city', true, false);
    formik.setFieldTouched('billing.internationalCityName', true, false);
    formik.setFieldTouched('billing.address', true, false);
    formik.setFieldTouched('billing.zip', true, false);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <Col xs={24} md={12}>
      <div className="modal-title">Billing location</div>

      <FormItem name="billing.locationSameAsWork" className="checkbox-wrapper">
        <Checkbox
          name="billing.locationSameAsWork"
          onChange={(e) => handleLocationSameAsWorkChange(e, formik)}
          disabled={isFormDisabled}
        >
          Same as work location
        </Checkbox>
      </FormItem>

      <Row gutter={20}>
        <Col xs={24} lg={12}>
          <FormItem name="billing.country" label="Country *">
            <FormikSelectWithLoading
              isLoading={isCountriesLoading}
              name="billing.country"
              showSearch
              placeholder="Select billing office country"
              optionFilterProp="children"
              onChange={(value: number) => handleCountryChange(value, formik)}
              value={getBillLocationValue(formik, mainLocation, 'country')}
              disabled={
                isFormDisabled || formik.values.billing.locationSameAsWork
              }
            >
              {countries.map((country) => (
                <FormikSelectWithLoading.Option
                  key={`select-country-state-${country.id}`}
                  value={country.id || -1}
                >
                  {country.name}
                </FormikSelectWithLoading.Option>
              ))}
            </FormikSelectWithLoading>
          </FormItem>
        </Col>
        {!isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="billing.state" label="State *">
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                name="billing.state"
                showSearch
                placeholder="Select billing office state"
                optionFilterProp="children"
                onChange={() => handleStateChange(formik)}
                value={getBillLocationValue(formik, mainLocation, 'state')}
                disabled={
                  isFormDisabled || formik.values.billing.locationSameAsWork
                }
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-billing-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>
        )}
        {isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="billing.internationalStateName" label="State *">
              <Input
                name="billing.internationalStateName"
                placeholder="Enter billing office state"
                value={getBillLocationValue(
                  formik,
                  mainLocation,
                  'internationalStateName'
                )}
                disabled={
                  isFormDisabled ||
                  formik.values.billing.locationSameAsWork ||
                  !formik.values.billing.country
                }
                autoComplete='off'
              />
            </FormItem>
          </Col>
        )}
        {isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem name="billing.internationalCityName" label="City *">
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name="billing.internationalCityName"
                filterOption
                value={
                  getBillLocationValue(
                    formik,
                    mainLocation,
                    'internationalCityName',
                  ) as string
                }
                disabled={
                  isFormDisabled ||
                  formik.values.billing.locationSameAsWork ||
                  !formik.values.billing.internationalStateName
                }
                showSearch
                placeholder="Enter billing office city"
              >
                {filteredInternationalCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`billing-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}
        {!isInternationalCountry && (
          <Col xs={24} lg={12}>
            <FormItem
              name="billing.city"
              label="City *"
              data-id="newAssignmentWorksiteCityField"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name="billing.city"
                filterOption
                placeholder="Select billing office city"
                showSearch
                value={
                  getBillLocationValue(formik, mainLocation, 'city') as string
                }
                disabled={
                  isFormDisabled ||
                  formik.values.billing.locationSameAsWork ||
                  !formik.values.billing.state
                }
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`billing-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}

        <Col xs={24} lg={12}>
          <FormItem
            label="Address *"
            data-id="newAssignmentBillingAddressField"
            name="billing.address"
          >
            <Input
              name="billing.address"
              placeholder="Enter billing address"
              value={
                getBillLocationValue(formik, mainLocation, 'address') as string
              }
              disabled={
                isFormDisabled || formik.values.billing.locationSameAsWork
              }
              autoComplete='off'
            />
          </FormItem>
        </Col>

        <Col xs={24} lg={12}>
          <FormItem
            label="ZIP code"
            data-id="newAssignmentBillingZipField"
            name="billing.zip"
          >
            <Input
              name="billing.zip"
              className="full-width"
              placeholder="Enter ZIP code"
              value={
                getBillLocationValue(formik, mainLocation, 'zip') as string
              }
              disabled={
                isFormDisabled || formik.values.billing.locationSameAsWork
              }
              autoComplete='off'
            />
          </FormItem>
        </Col>
      </Row>

      <FormItem
        label="Billing email *"
        data-id="newAssignmentBillingEmailField"
        name="billing.email"
      >
        <Input
          name="billing.email"
          placeholder="Enter billing email"
          disabled={isFormDisabled}
          autoComplete='off'
        />
      </FormItem>
    </Col>
  );
}
